import type { OnInit} from '@angular/core';
import { Component, signal, computed } from '@angular/core';

import {
  ButtonComponent, SearchBarComponent, SpinnerComponent, EmptyStateComponent,
  DataTableComponent, BadgeComponent, ContentTypeBadgeComponent, PaginationComponent,
} from '@echospaces-web-monorepo/es-shared-ui';

interface PlaceholderLink {
  placeholderId: string;
  placeholderSlug: string;
  placeholderType: string;
  spaceSlug: string;
  spacePath: string;
  contentTitle: string | null;
  contentType: string | null;
  linked: boolean;
}

/**
 * Displays the full placeholder-to-content link table for a tenant.
 * Allows admins to inspect which UE placeholders are linked to CMS content objects.
 */
@Component({
  selector: 'es-placeholders-links-page',
  imports: [
    ButtonComponent, SearchBarComponent, SpinnerComponent, EmptyStateComponent,
    DataTableComponent, BadgeComponent, ContentTypeBadgeComponent, PaginationComponent,
  ],
  template: `
    <div class="max-w-6xl mx-auto">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold font-exo">Placeholders & Links</h1>
          <p class="text-sm text-[var(--text-secondary)]">{{ placeholderLinks().length }} placeholders</p>
        </div>
      </div>

      <es-search-bar
        class="mb-4 block"
        placeholder="Search placeholders or content..."
        [(value)]="searchQuery"
      ></es-search-bar>

      @if (loading()) {
        <div class="flex justify-center py-12">
          <es-spinner label="Loading placeholders..."></es-spinner>
        </div>
      } @else if (filtered().length === 0) {
        <es-empty-state
          title="No placeholders"
          description="Placeholders are created by UE developers via the editor plugin."
          icon="📌"
        ></es-empty-state>
      } @else {
        <div class="overflow-auto rounded-lg border border-[var(--border)]">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-[var(--bg-secondary)]">
                <th class="px-4 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase">Placeholder</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase">Space Path</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase">Type</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase">Linked Content</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              @for (item of paginated(); track item.placeholderId) {
                <tr class="border-t border-[var(--border)] hover:bg-[var(--bg-card-hover)] transition-colors">
                  <td class="px-4 py-3 font-medium">{{ item.placeholderSlug }}</td>
                  <td class="px-4 py-3 text-[var(--text-secondary)] font-mono text-xs">{{ item.spacePath }}</td>
                  <td class="px-4 py-3"><es-badge [label]="item.placeholderType" variant="secondary"></es-badge></td>
                  <td class="px-4 py-3">
                    @if (item.contentTitle) {
                      <div class="flex items-center gap-2">
                        @if (item.contentType) {
                          <es-content-type-badge [type]="$any(item.contentType)"></es-content-type-badge>
                        }
                        <span>{{ item.contentTitle }}</span>
                      </div>
                    } @else {
                      <span class="text-[var(--text-secondary)] italic">Unlinked</span>
                    }
                  </td>
                  <td class="px-4 py-3">
                    <es-badge
                      [label]="item.linked ? 'Linked' : 'Empty'"
                      [variant]="item.linked ? 'success' : 'warning'"
                    ></es-badge>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="mt-4">
          <es-pagination
            [total]="filtered().length"
            [page]="page()"
            [pageSize]="pageSize"
            (pageChange)="page.set($event)"
          ></es-pagination>
        </div>
      }
    </div>
  `,
})
export class PlaceholdersLinksPageComponent implements OnInit {
  placeholderLinks = signal<PlaceholderLink[]>([]);
  loading = signal(false);
  searchQuery = signal('');
  page = signal(1);
  pageSize = 20;

  filtered = computed(() => {
    const q = this.searchQuery().toLowerCase();
    if (!q) return this.placeholderLinks();
    return this.placeholderLinks().filter(a =>
      a.placeholderSlug.toLowerCase().includes(q) ||
      a.spacePath.toLowerCase().includes(q) ||
      (a.contentTitle?.toLowerCase().includes(q) ?? false));
  });

  paginated = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filtered().slice(start, start + this.pageSize);
  });

  async ngOnInit() {
    this.loading.set(true);
    try {
      this.placeholderLinks.set([]);
    } finally {
      this.loading.set(false);
    }
  }
}
